import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStoredUser, logout } from '@/hooks/useAuth';
import { api, authFetch, API_BASE_URL } from '@/services/api';
import { useSelectedAsset, SelectedAsset } from '@/hooks/useSelectedAsset';
import { useTheme } from '@/hooks/useTheme';
import { localizeRegion } from '@/utils/region';
import SkoBellBadge from './SkoBellBadge';

interface TopbarProps {
  sidebarWidth: number;
  breadcrumb?: string[];
  language: string;
  onLanguageChange: (lang: string) => void;
  onMobileMenuToggle: () => void;
  isMobile: boolean;
}

interface AssetItem {
  id: string;
  assetName: string;
  installedCapacityMw: number;
  region: string;
  operationalStatus: string;
  counterpartyShortName?: string;
  counterpartyName?: string;
}

export default function Topbar({ sidebarWidth, breadcrumb = [], language, onLanguageChange, onMobileMenuToggle, isMobile }: TopbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // The station selector is only meaningful on /forecast (switch the analysed SES).
  // It is hidden on every other page.
  const hideStationSelector = !location.pathname.startsWith('/forecast');
  // Route -> header breadcrumb. Falls back to the page-provided `breadcrumb` prop.
  const ROUTE_CRUMBS: Record<string, string[]> = {
    '/dashboard/owner': [t('d_bc_home'), t('nav_owner_home')],
    '/forecast': [t('d_bc_dispatch'), t('d_bc_forecast')],
    '/bess': [t('d_bc_dispatch'), 'BESS'],
    '/market/ueex': [t('d_bc_analytics'), t('d_bc_market')],
    '/info': [t('d_bc_analytics'), t('d_bc_info')],
    '/assets': [t('d_bc_home'), t('d_bc_assets')],
  };
  const crumbs = ROUTE_CRUMBS[location.pathname] ?? breadcrumb;
  const currentUser = getStoredUser();
  const [notifOpen, setNotifOpen] = useState(false);
  const [stationOpen, setStationOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const { selectedAsset, setSelectedAsset } = useSelectedAsset();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    // Owners (CEO) get only their portfolio via the scoped /owner/stations;
    // other roles keep the full /assets list.
    const isOwner = currentUser?.role === 'CEO';
    const load: Promise<AssetItem[]> = isOwner
      ? authFetch(`${API_BASE_URL}/owner/stations`).then((r) => r.json()).then((d) => (d.stations ?? []) as AssetItem[])
      : api.get<{ data: AssetItem[] } | AssetItem[]>('/assets')
          .then((res) => (Array.isArray(res) ? res : (res as { data: AssetItem[] }).data ?? []));
    load
      .then((list) => {
        setAssets(list);
        const stored = selectedAsset;
        const inScope = stored && list.some((a) => a.id === stored.id);
        // Pick first if nothing stored OR the stored station is outside this account's scope.
        if (list.length > 0 && (!stored || !inScope)) {
          const first = list[0];
          setSelectedAsset({
            id: first.id,
            assetName: first.assetName,
            installedCapacityMw: first.installedCapacityMw,
            region: first.region,
            operationalStatus: first.operationalStatus,
            counterpartyShortName: first.counterpartyShortName,
            counterpartyName: first.counterpartyName,
          });
        }
      })
      .catch(() => {
        setAssets([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectAsset = (asset: AssetItem) => {
    const mapped: SelectedAsset = {
      id: asset.id,
      assetName: asset.assetName,
      installedCapacityMw: asset.installedCapacityMw,
      region: asset.region,
      operationalStatus: asset.operationalStatus,
      counterpartyShortName: asset.counterpartyShortName,
      counterpartyName: asset.counterpartyName,
    };
    setSelectedAsset(mapped);
    setStationOpen(false);
  };

  const userMenuItems = [
    { icon: 'ri-user-line', key: 'topbar_profile' },
    { icon: 'ri-settings-3-line', key: 'topbar_settings' },
    { icon: 'ri-building-line', key: 'topbar_company' },
    { icon: 'ri-team-line', key: 'topbar_team' },
  ];

  const notifications = [
    { icon: 'ri-error-warning-line', textKey: 'notif_inverter_alarm', timeKey: 'notif_2h', color: '#D12229' },
    { icon: 'ri-file-text-line', textKey: 'notif_mms_confirmed', timeKey: 'notif_yesterday', color: '#0D7A3C' },
    { icon: 'ri-mail-line', textKey: 'notif_market_digest', time: '09:15', color: '#1F5B8F' },
  ];

  const topbarBg = isDark ? '#1A1A1A' : '#fff';
  const topbarBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : '#F5F5F7';
  const dropdownBg = isDark ? '#1A1A1A' : '#fff';
  const dropdownBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const dropdownHover = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const separatorColor = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const langBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';

  return (
    <header
      className="fixed top-0 right-0 z-20 flex items-center px-4 md:px-6 h-[72px]"
      style={{
        left: isMobile ? 0 : `${sidebarWidth}px`,
        background: topbarBg,
        borderBottom: `1px solid ${topbarBorder}`,
        transition: 'left 0.3s',
      }}
    >
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={onMobileMenuToggle}
          className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer mr-3 shrink-0"
          style={{ color: textMain }}
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
      )}

      {/* LEFT: breadcrumb + station selector */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {crumbs.length > 0 && (
          <div className="hidden sm:flex items-center gap-1 text-sm" style={{ color: textMuted, fontFamily: 'Inter, sans-serif' }}>
            {crumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <span className="text-xs">/</span>}
                <span className={i === crumbs.length - 1 ? 'font-medium' : ''} style={{ color: i === crumbs.length - 1 ? textMain : textMuted }}>
                  {crumb}
                </span>
              </span>
            ))}
          </div>
        )}
        {crumbs.length > 0 && !hideStationSelector && <div className="hidden sm:block mx-3 h-4" style={{ borderLeft: `1px solid ${separatorColor}` }}></div>}

        {/* Station / Asset selector — hidden in the owner cabinet (non-functional there) */}
        {!hideStationSelector && (
        <div className="relative min-w-0">
          <button
            onClick={() => setStationOpen(!stationOpen)}
            className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-md text-sm cursor-pointer transition-colors whitespace-nowrap"
            style={{
              background: stationOpen ? hoverBg : 'transparent',
              border: `1px solid ${inputBorder}`,
              color: textMain,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: selectedAsset?.operationalStatus === 'active' ? '#0D7A3C' : '#6B6B70' }}
            ></div>
            <span className="font-medium truncate max-w-[120px] sm:max-w-none">
              {selectedAsset ? selectedAsset.assetName : '—'}
            </span>
            {selectedAsset && (
              <span className="hidden sm:inline" style={{ color: textMuted }}>· {selectedAsset.installedCapacityMw} МВт</span>
            )}
            <i className="ri-arrow-down-s-line text-sm shrink-0" style={{ color: textMuted }}></i>
          </button>

          {stationOpen && (
            <div
              className="absolute top-full mt-1 left-0 rounded-lg z-50 py-1 min-w-[240px]"
              style={{ background: dropdownBg, border: `1px solid ${dropdownBorder}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
            >
              {assets.length === 0 ? (
                <div className="px-4 py-3 text-sm" style={{ color: textMuted, fontFamily: 'Inter, sans-serif' }}>
                  <i className="ri-flashlight-line mr-2"></i>
                  No assets found
                </div>
              ) : (
                assets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => handleSelectAsset(asset)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif', color: textMain }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: asset.operationalStatus === 'active' ? '#0D7A3C' : '#6B6B70' }}
                    ></div>
                    <div>
                      <div className="font-medium">{asset.assetName}</div>
                      <div className="text-xs" style={{ color: textMuted }}>
                        {asset.installedCapacityMw} МВт · {localizeRegion(asset.region, language)}
                      </div>
                    </div>
                    {selectedAsset?.id === asset.id && (
                      <i className="ri-check-line ml-auto text-sm" style={{ color: '#0D7A3C' }}></i>
                    )}
                  </button>
                ))
              )}
              <div style={{ borderTop: `1px solid ${separatorColor}` }} className="mt-1 pt-1">
                <button
                  onClick={() => { navigate('/assets'); setStationOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                  style={{ color: '#D12229', fontFamily: 'Inter, sans-serif' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="ri-add-line text-sm"></i>
                  {t('topbar_add_station')}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* CENTER: search + language + theme */}
      <div className="flex items-center shrink-0">
      {/* Search — no ⌘K button */}
      <div className="relative mr-4 hidden sm:block">
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all"
          style={{
            background: searchFocused ? (isDark ? '#1A1A1A' : '#fff') : inputBg,
            border: `1px solid ${searchFocused ? '#D12229' : inputBorder}`,
            width: searchFocused ? '260px' : '200px',
            transition: 'all 0.2s',
          }}
        >
          <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
        <input
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder={t('topbar_search')}
            className="bg-transparent outline-none text-sm flex-1"
            style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}
          />
        </div>
      </div>

      {/* Language switch */}
      <div className="hidden md:flex items-center gap-0.5 mr-4 rounded-md overflow-hidden" style={{ border: `1px solid ${langBorder}` }}>
        {['UA', 'PL', 'EN'].map((lang) => (
          <button
            key={lang}
            onClick={() => onLanguageChange(lang)}
            className="px-2.5 py-1 text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap"
            style={{
              background: language === lang ? '#D12229' : 'transparent',
              color: language === lang ? '#fff' : textMuted,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {lang}
          </button>
        ))}
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-all"
        style={{
          background: isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7',
          color: isDark ? '#FFD700' : '#6B6B70',
        }}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : '#EBEBEE'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7'; }}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`text-sm ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
        </div>
      </button>
      </div>

      {/* RIGHT: alerts + notifications + user */}
      <div className="flex items-center justify-end flex-1">
      <SkoBellBadge />
      {/* Notifications */}
      <div className="relative mr-3">
        <button
          onClick={() => { setNotifOpen(!notifOpen); setUserOpen(false); setStationOpen(false); }}
          className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors relative"
          style={{ background: notifOpen ? hoverBg : 'transparent', border: '1px solid transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={(e) => { if (!notifOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          <i className="ri-notification-3-line text-lg" style={{ color: textMain }}></i>
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: '#D12229' }}></span>
        </button>
        {notifOpen && (
          <div
            className="absolute top-full right-0 mt-2 w-80 rounded-xl z-50 overflow-hidden"
            style={{ background: dropdownBg, border: `1px solid ${dropdownBorder}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${separatorColor}` }}>
              <span className="font-semibold text-sm" style={{ fontFamily: 'Inter, sans-serif', color: textMain }}>
                {t('topbar_notifications')}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: '#D12229', color: '#fff' }}>3</span>
            </div>
            {notifications.map((n, i) => (
              <div
                key={i}
                className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors"
                style={{ borderBottom: i < notifications.length - 1 ? `1px solid ${separatorColor}` : 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className={`${n.icon} text-base mt-0.5`} style={{ color: n.color }}></i>
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
                    {t(n.textKey)}
                  </div>
                  <div className="text-xs" style={{ color: textMuted }}>
                    {n.timeKey ? t(n.timeKey) : n.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User avatar */}
      <div className="relative">
        <button
          onClick={() => { setUserOpen(!userOpen); setNotifOpen(false); setStationOpen(false); }}
          className="flex items-center gap-2 px-2 py-1 rounded-lg cursor-pointer transition-colors"
          style={{ background: userOpen ? hoverBg : 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={(e) => { if (!userOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ background: '#D12229' }}
          >
            {currentUser?.avatar ?? 'US'}
          </div>
          <div className="hidden lg:block text-left">
            <div className="text-sm font-medium leading-tight" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
              {currentUser
                ? currentUser.displayName.split(' ')[0] + ' ' + (currentUser.displayName.split(' ')[1]?.[0] ?? '') + '.'
                : t('user_guest')}
            </div>
            <div className="text-[11px]" style={{ color: textMuted }}>{currentUser?.role ?? ''}</div>
          </div>
          <i className="ri-arrow-down-s-line text-sm hidden lg:block" style={{ color: textMuted }}></i>
        </button>
        {userOpen && (
          <div
            className="absolute top-full right-0 mt-2 w-56 rounded-xl z-50 overflow-hidden"
            style={{ background: dropdownBg, border: `1px solid ${dropdownBorder}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
          >
            <div className="px-4 py-3" style={{ borderBottom: `1px solid ${separatorColor}` }}>
              <div className="text-sm font-semibold" style={{ fontFamily: 'Inter, sans-serif', color: textMain }}>
                {currentUser?.displayName ?? t('user_guest')}
              </div>
              <div className="text-xs" style={{ color: textMuted }}>{currentUser?.email ?? ''}</div>
            </div>
            {userMenuItems.map((item) => (
              <button
                key={item.key}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors"
                style={{ fontFamily: 'Inter, sans-serif', color: textMain }}
                onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className={`${item.icon} text-sm`} style={{ color: textMuted }}></i>
                {t(item.key)}
              </button>
            ))}
            <div style={{ borderTop: `1px solid ${separatorColor}` }} className="mt-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors whitespace-nowrap"
                style={{ color: '#D12229', fontFamily: 'Inter, sans-serif' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = dropdownHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className="ri-logout-box-r-line text-sm"></i>
                {t('topbar_logout')}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </header>
  );
}